// useUserActivity — FE-hook för #96 user_activity-logging
// (POST_QA_SUBPLAN Wave C.2).
//
// Anrop:
//
//   const { log } = useUserActivity();
//   await log('save', { sheetCount: 4, isCloud: true });
//
// Edge-fn:en (log_user_activity) gör opt-in-checken — om callerId inte
// är opted-in returnerar den `{ success: true, skipped: 'opt_out' }`.
// Call-sites behöver INTE gate:a sina log()-anrop med en
// opt-in-precheck. Det förenklar instrumentering.
//
// **Fire-and-forget**: log() returnerar en Promise men call-sites
// behöver inte await:a. Misslyckanden skickas till `logger.warn` så
// dev-console:n ser dem, men anroparen ska inte ha en kritisk path
// som beror på att lyckas.

import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/** Tillåtna event-kinds. Speglar enum i migrationen
 *  20260622203042_user_activity.sql.  */
export type UserActivityKind = 'save' | 'ai-call' | 'export';

export interface UserActivityResult {
  success: boolean;
  /** 'opt_out' om opt-in är false; undefined annars. */
  skipped?: string;
  error?: string;
}

export interface UseUserActivity {
  log: (
    kind: UserActivityKind,
    meta?: Record<string, unknown>,
  ) => Promise<UserActivityResult>;
}

export function useUserActivity(): UseUserActivity {
  const log = useCallback(
    async (
      kind: UserActivityKind,
      meta: Record<string, unknown> = {},
    ): Promise<UserActivityResult> => {
      try {
        const { data, error } = await supabase.functions.invoke('log_user_activity', {
          body: { kind, meta },
        });
        if (error) {
          logger.warn(`[user-activity] invoke failed (kind=${kind}):`, error.message);
          return { success: false, error: error.message };
        }
        return (data as UserActivityResult) ?? { success: true };
      } catch (e) {
        // Network/DOM-fel — log:a men låt callern fortsätta.
        logger.warn(`[user-activity] threw (kind=${kind}):`, e);
        return { success: false, error: e instanceof Error ? e.message : 'unknown' };
      }
    },
    [],
  );

  return { log };
}
